import React from "react";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, FileText, AlertCircle } from "lucide-react";
import useTitanApp from "@/hooks/useTitanApp";

interface NHS111ReferralTabProps {
  isDarkMode?: boolean;
  referralData: {
    referralDateTime: string;
    referredFrom: string;
    nhsCaseId: string;
    clinicalSummary: string;
    repeatPrescriptionRequired: boolean;
    patientReportedCondition: string;
    pathwaysDisposition: string;
    consultationSummary: string;
    pathwaysAssessment: string;
  };
}

export function NHS111ReferralTab({ isDarkMode }: NHS111ReferralTabProps) {
  const { pharmacyServiceState } = useTitanApp();
  const { NHS111ReferralData } = pharmacyServiceState;

  const sectionClass = `mb-6 p-4 rounded-lg border transition-colors duration-300 ${
    isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
  }`;

  const labelClass = `text-xs font-medium mb-1 transition-colors duration-300 ${
    isDarkMode ? "text-gray-400" : "text-gray-500"
  }`;

  const valueClass = `text-sm transition-colors duration-300 ${
    isDarkMode ? "text-white" : "text-gray-900"
  }`;

  const headingClass = `text-sm font-semibold mb-3 transition-colors duration-300 ${
    isDarkMode ? "text-white" : "text-gray-900"
  }`;

  const now = new Date();
  const referralDate = new Date(
    now.getTime() - Math.random() * 24 * 60 * 60 * 1000,
  );
  const referralData = NHS111ReferralData;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className={sectionClass}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className={headingClass}>Referral Information</h3>
            <p
              className={`text-xs transition-colors duration-300 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Details from NHS 111 Digital Message Engine
            </p>
          </div>
          <Badge variant="secondary" className="text-blue-700 bg-blue-100">
            NHS 111
          </Badge>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-3 h-3 text-blue-500" />
              <label className={labelClass}>Referral Date and Time</label>
            </div>
            <p className={valueClass}>
              {referralData.dateandTime
                ? new Date(referralData.dateandTime).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  })
                : ""}
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-3 h-3 text-blue-500" />
              <label className={labelClass}>NHS Case ID</label>
            </div>
            <p className={valueClass}>{referralData.nhsCaseID}</p>
          </div>

          <div className="col-span-2">
            <label className={labelClass}>Referred From</label>
            <p className={valueClass}>{referralData.referredFrom}</p>
          </div>
        </div>
      </div>

      {/* Clinical Summary */}
      <div className={sectionClass}>
        <h3 className={headingClass}>Clinical Summary</h3>
        <p className={valueClass} style={{ whiteSpace: "pre-line" }}>
          {referralData.clinicalSummary.replaceAll("\\n", "\n")}
        </p>
      </div>

      {/* Prescription Requirement */}
      <div className={sectionClass}>
        <div className="flex items-start gap-3">
          <AlertCircle
            className={`w-5 h-5 mt-0.5 ${
              referralData.repeatPrescriptionRequired
                ? "text-amber-500"
                : "text-gray-400"
            }`}
          />
          <div>
            <h3 className={headingClass}>
              Repeat Prescription Required Within 12 Hours
            </h3>
            <p className={valueClass}>
              {referralData.repeatPrescriptionRequired ? "Yes" : "No"}
            </p>
          </div>
        </div>
      </div>

      {/* Patient's Reported Condition */}
      <div className={sectionClass}>
        <h3 className={headingClass}>Patient's Reported Condition</h3>
        <p className={valueClass} style={{ whiteSpace: "pre-line" }}>
          {referralData.reportedCondition.replaceAll("\\n", "\n")}
        </p>
      </div>

      {/* Pathways Disposition */}
      <div className={sectionClass}>
        <h3 className={headingClass}>Pathways Disposition</h3>
        <p className={valueClass} style={{ whiteSpace: "pre-line" }}>
          {referralData.pathwaysDisposition.replaceAll("\\n", "\n")}
        </p>
      </div>

      {/* Consultation Summary */}
      <div className={sectionClass}>
        <h3 className={headingClass}>Consultation Summary</h3>
        <p className={valueClass} style={{ whiteSpace: "pre-line" }}>
          {referralData.consultationSummary.replaceAll("\\n", "\n")}
        </p>
      </div>

      {/* Pathways Assessment */}
      <div className={sectionClass}>
        <h3 className={headingClass}>Pathways Assessment</h3>
        <div className={`${valueClass} whitespace-pre-line`}>
          {referralData.pathwaysAssessment}
        </div>
      </div>
    </div>
  );
}
